import { merge } from 'lodash';
import {
  useMutation,
  UseMutationOptions,
  UseMutationResult,
  useQueryClient,
} from 'react-query';
import { ProjectId } from 'api/models';
import { PhaseAPI } from 'api/phase';
import { Project, ProjectAPI, ProjectPhase } from 'api/project';
import { getProjectDependencyMapKey, getProjectDetailQueryKey } from './common';

const useCreateProjectPhaseMutation = ([
  ,
  projectId,
]: ProjectId): UseMutationResult<
  ProjectPhase,
  unknown,
  Partial<ProjectPhase>
> => {
  const queryClient = useQueryClient();

  return useMutation(
    (payload) => ProjectAPI.createProjectPhase(projectId, payload),
    {
      onSuccess: async () => {
        await queryClient.refetchQueries(
          getProjectDetailQueryKey(ProjectId(projectId)),
        );
      },
    },
  );
};

export type UpdateProjectPhasePayload = Partial<ProjectPhase> &
  Pick<ProjectPhase, 'projectPhaseId'>;

const useUpdateProjectPhaseMutation = ([
  ,
  projectId,
]: ProjectId): UseMutationResult<
  ProjectPhase,
  unknown,
  UpdateProjectPhasePayload
> => {
  const queryClient = useQueryClient();
  const projectQueryKey = getProjectDetailQueryKey(ProjectId(projectId));

  return useMutation<
    ProjectPhase,
    unknown,
    UpdateProjectPhasePayload,
    { previous: Project | undefined }
  >(
    ({ projectPhaseId, ...payload }) =>
      ProjectAPI.updateProjectPhase(projectId, projectPhaseId, payload),
    {
      onMutate: async (payload) => {
        await queryClient.cancelQueries(projectQueryKey);
        const previous = queryClient.getQueryData<Project>(projectQueryKey);

        // Optimistically update to the new value
        if (previous) {
          queryClient.setQueryData<Project>(projectQueryKey, () => ({
            ...previous,
            projectPhases: [...previous.projectPhases].map((phase) => {
              return phase.projectPhaseId === payload.projectPhaseId
                ? merge(phase, payload)
                : phase;
            }),
          }));
        }

        // Return a context object with the snapshotted value
        return { previous };
      },
      onSuccess: async () => {
        await queryClient.refetchQueries(projectQueryKey);
      },
      onError: (err, projectphasePayload, context) => {
        queryClient.setQueryData(projectQueryKey, context?.previous);
      },
    },
  );
};

const useDeleteProjectPhaseMutation = (): UseMutationResult<
  void,
  unknown,
  { projectPhaseId: number; projectId: ProjectId }
> => {
  const queryClient = useQueryClient();

  return useMutation(
    ({ projectPhaseId, projectId: [, projectId] }) =>
      ProjectAPI.deleteProjectPhase(projectId, projectPhaseId),
    {
      onSuccess: async (_, { projectId }) => {
        await queryClient.refetchQueries(getProjectDetailQueryKey(projectId));
      },
    },
  );
};

const useCreatePhaseDependencyMutation = (
  projectId: number,
  options: UseMutationOptions<
    ProjectPhase,
    unknown,
    {
      phaseId: number;
      dependentPhaseId: number;
      isSync: boolean;
      toggleUpdating: (_value) => void;
    }
  > = {},
): UseMutationResult<ProjectPhase, unknown, any> => {
  const queryClient = useQueryClient();

  return useMutation(
    ({ phaseId, dependentPhaseId, isSync, toggleUpdating }) =>
      PhaseAPI.createDependentPhase(
        phaseId,
        {
          dependentProjectPhaseIds: [dependentPhaseId],
        },
        isSync,
        toggleUpdating,
      ),
    {
      onSuccess: async () => {
        await queryClient.refetchQueries(getProjectDependencyMapKey(projectId));
      },
      ...options,
    },
  );
};

const useDeletePhaseDependencyMutation = (
  projectId: number,
  options: UseMutationOptions<
    boolean,
    unknown,
    { phaseId: number; dependentPhaseId: number }
  > = {},
): UseMutationResult<boolean, unknown, any> => {
  const queryClient = useQueryClient();

  return useMutation(
    ({ phaseId, dependentPhaseId }) =>
      PhaseAPI.removeDependentPhase(phaseId, {
        dependentProjectPhaseIds: [dependentPhaseId],
      }),
    {
      onSuccess: async () => {
        await queryClient.refetchQueries(getProjectDependencyMapKey(projectId));
      },
      ...options,
    },
  );
};

export {
  useCreateProjectPhaseMutation,
  useUpdateProjectPhaseMutation,
  useDeleteProjectPhaseMutation,
  useCreatePhaseDependencyMutation,
  useDeletePhaseDependencyMutation,
};
